import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Fuse from 'fuse.js';
import { map } from 'rxjs';
import { ClickOutsideDirective } from '../../directives/click-outside/click-outside.directive';
import { Country, Region } from '../../interfaces/cached-content';
import { GbTheme, THEME_SIGNAL } from '../../interfaces/theme';

@Component({
  selector: 'app-animated-search',
  imports: [CommonModule, RouterModule, TranslatePipe, ClickOutsideDirective],
  templateUrl: './animated-search.component.html',
  styleUrl: './animated-search.component.scss',
})
export class AnimatedSearchComponent {
  // Inputs
  public placeholder = input.required<string>();
  public items = input.required<(Country | Region)[]>();
  public isMobileSearchActive = input<boolean>();
  public isMobileView = input<boolean>();
  public theme = inject(THEME_SIGNAL);

  public GbTheme = GbTheme;

  private readonly translateService = inject(TranslateService);

  // Track current language and translation loading state
  private readonly currentLanguage = toSignal(
    this.translateService.onLangChange.pipe(map((result) => result.lang)),
    { initialValue: 'en' }
  );

  // Model
  public searchValue = model<string>('');

  // Signals
  public isSearchActive = signal(false);
  public isDropdownOpen = signal(false);

  // Output
  public itemSelected = output<Country | Region>();

  // Fuse.js for fuzzy search
  private fuse: Fuse<Country | Region> | null = null;

  // Computed filtered items using Fuse.js
  public filteredSearchResults = computed(() => {
    const searchTerm = this.searchValue().trim();
    const allSearchableItems = this.items();

    if (!searchTerm) {
      return allSearchableItems;
    }

    if (!this.fuse) {
      this.initializeFuseSearchEngine(allSearchableItems);
    }

    if (this.fuse) {
      const searchResults = this.fuse.search(searchTerm);
      return searchResults.map((result) => result.item);
    }

    return allSearchableItems;
  });

  constructor() {
    // Effect to clear search when items change (tab switch) or mobile search state changes
    effect(() => {
      const currentSearchableItems = this.items();
      this.isMobileSearchActive();

      this.initializeFuseSearchEngine(currentSearchableItems);
      this.clearSearchAndCloseDropdown();
    });

    // Effect to reinitialize search engine when language changes
    effect(() => {
      const currentLang = this.currentLanguage();
      const currentItems = this.items();

      if (currentLang && currentItems.length > 0) {
        // Reinitialize search engine with new translations
        this.initializeFuseSearchEngine(currentItems);
      }
    });
  }

  private initializeFuseSearchEngine(items: (Country | Region)[]): void {
    const fuseOptions = {
      keys: ['name', 'translated_name'],
      threshold: 0.1,
      includeScore: true,
      includeMatches: true,
    };

    const translatedItems = items.map((item) => {
      let translatedName = item.name; // fallback to original name

      try {
        const translationKey =
          item.regionType === 'region'
            ? `region_names.${item.slug}`
            : `country_names.${(item as Country).iso3code}`;

        const translation = this.translateService.instant(translationKey);

        // Check if translation was successful (not just returning the key)
        if (translation && translation !== translationKey) {
          translatedName = translation;
        } else {
          // Translation not available yet, use original name
          console.warn(
            'Translation not available for:',
            translationKey,
            'using fallback:',
            item.name
          );
        }
      } catch (error) {
        console.warn('Translation failed for item:', item.name, error);
        // Use original name as fallback
      }

      return {
        ...item,
        translated_name: translatedName,
      };
    });

    this.fuse = new Fuse(translatedItems, fuseOptions);
  }

  public toggleSearch(): void {
    this.isSearchActive.set(!this.isSearchActive());
    if (!this.isSearchActive()) {
      this.isDropdownOpen.set(false);
      this.searchValue.set('');
    }
  }

  public handleSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      const inputValue = target.value;
      this.searchValue.set(inputValue);
      this.isDropdownOpen.set(!!(inputValue && inputValue.length > 0));
    }
  }

  public openDropdown(): void {
    this.isDropdownOpen.set(true);
  }

  public selectItem(item: Country | Region): void {
    this.itemSelected.emit(item);
    this.isDropdownOpen.set(false);
    this.isSearchActive.set(false);
    this.searchValue.set('');
  }

  public getCountryIso3code(item: Country | Region): string {
    return (item as Country).iso3code;
  }

  public clearSearchAndCloseDropdown(): void {
    this.searchValue.set('');
    this.isDropdownOpen.set(false);
  }

  public onClickOutside(): void {
    if (!this.isDropdownOpen() && !this.isSearchActive()) {
      return;
    }
    this.isSearchActive.set(false);
    this.isDropdownOpen.set(false);
    this.searchValue.set('');
  }
}
